import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { z } from 'zod';

import type { CreateFlowPayload } from '@polyai/stream/commands/handlers/flow/create-flow/schema';
import type { CreateFlowStepPayload } from '@polyai/stream/commands/handlers/flow/create-flow-step/schema';
import type { CreateNoCodeConditionPayload } from '@polyai/stream/commands/handlers/flow/create-no-code-condition/schema';
import type { CreateNoCodeStepPayload } from '@polyai/stream/commands/handlers/flow/create-no-code-step/schema';
import type { CreateStepPayload } from '@polyai/stream/commands/handlers/flow/create-step/schema';
import type { CreateStepConditionPayload } from '@polyai/stream/commands/handlers/flow/create-step-condition/schema';
import type { CreateTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/create-transition-function/schema';
import type { DeleteFlowPayload } from '@polyai/stream/commands/handlers/flow/delete-flow/schema';
import type { DeleteFlowStepPayload } from '@polyai/stream/commands/handlers/flow/delete-flow-step/schema';
import type { DeleteNoCodeConditionPayload } from '@polyai/stream/commands/handlers/flow/delete-no-code-condition/schema';
import type { DeleteNoCodeStepPayload } from '@polyai/stream/commands/handlers/flow/delete-no-code-step/schema';
import type { DeleteStepPayload as DeleteGenericStepPayload } from '@polyai/stream/commands/handlers/flow/delete-step/schema';
import type { DeleteStepConditionPayload } from '@polyai/stream/commands/handlers/flow/delete-step-condition/schema';
import type { DeleteTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/delete-transition-function/schema';
import type { ImportFlowPayload } from '@polyai/stream/commands/handlers/flow/import-flow/schema';
import type { UpdateNoCodeConditionExitFlowPositionPayload } from '@polyai/stream/commands/handlers/flow/update-exit-flow-position/schema';
import type { UpdateFlowPayload } from '@polyai/stream/commands/handlers/flow/update-flow/schema';
import type { UpdateFlowStepPayload } from '@polyai/stream/commands/handlers/flow/update-flow-step/schema';
import type { UpdateNoCodeConditionPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-condition/schema';
import type { UpdateNoCodeConditionPositionPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-condition-position/schema';
import type { UpdateNoCodeStepPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-step/schema';
import type { UpdateNoCodeStepPositionPayload } from '@polyai/stream/commands/handlers/flow/update-no-code-step-position/schema';
import type { UpdateStepPayload } from '@polyai/stream/commands/handlers/flow/update-step/schema';
import type { UpdateFlowStepAsrConfigPayload } from '@polyai/stream/commands/handlers/flow/update-step-asr/schema';
import type { UpdateStepConditionPayload } from '@polyai/stream/commands/handlers/flow/update-step-condition/schema';
import type { UpdateStepConditionPositionPayload } from '@polyai/stream/commands/handlers/flow/update-step-condition-position/schema';
import type { UpdateFlowStepDTMFConfigPayload } from '@polyai/stream/commands/handlers/flow/update-step-dtmf/schema';
import type { UpdateFlowStepPositionPayload } from '@polyai/stream/commands/handlers/flow/update-step-position/schema';
import type { UpdateFlowStepPositionsPayload } from '@polyai/stream/commands/handlers/flow/update-step-positions/schema';
import type { UpdateFlowTransitionFunctionPayload } from '@polyai/stream/commands/handlers/flow/update-transition-function/schema';
import type { UpdateFlowTransitionFunctionLatencyControlPayload } from '@polyai/stream/commands/handlers/flow/update-transition-function-latency-control/schema';
import {
  createEntity,
  deleteEntity,
  updateEntity,
} from '@polyai/stream/store/reducers/entities/actions';
import {
  createFlow,
  createFlowStep,
  createFlowTransitionFunction,
  createNoCodeCondition,
  createNoCodeStep,
  createStep,
  createStepCondition,
  deleteFlow,
  deleteFlowStep,
  deleteFlowTransitionFunction,
  deleteNoCodeCondition,
  deleteNoCodeStep,
  deleteStep,
  deleteStepCondition,
  importFlow,
  moveFlowComponent,
  moveFlowComponents,
  updateFlow,
  updateFlowStep,
  updateFlowStepASRConfig,
  updateFlowStepDTMFConfig,
  updateFlowTransitionFunction,
  updateFlowTransitionFunctionLatencyControl,
  updateNoCodeCondition,
  updateNoCodeConditionExitFlowPosition,
  updateNoCodeConditionPosition,
  updateNoCodeStep,
  updateNoCodeStepPosition,
  updateStep,
  updateStepCondition,
  updateStepConditionPosition,
  updateStepPosition,
  updateStepPositions,
} from '@polyai/stream/store/reducers/flows/actions';
import {
  advancedStepRefAdapter,
  defaultStepRefAdapter,
  flowStepsAdapter,
  flowsAdapter,
  transitionFunctionRefAdapter,
  transitionFunctionsAdapter,
} from '@polyai/stream/store/reducers/flows/adapters';
import type {
  AdvancedStep,
  Condition,
  DefaultStep,
  Flow,
  FlowFunction,
  FunctionStep,
  Step,
} from '@polyai/stream/store/reducers/flows/types';
import type {
  StepAsrBiasing,
  StepDTMFConfig,
} from '@polyai/stream/store/reducers/flows/types/config';
import { assignDefinedFields } from '@polyai/stream/store/utils/assign-fields';
import { assignMetadata } from '@polyai/stream/store/utils/assign-metadata';
import {
  escapeForDoubleQuotedString,
  escapeForSingleQuotedString,
  replaceFlowNameInGotoFlow,
} from '@polyai/stream/store/utils/functions-helpers';

import {
  delayResponsesAdapter,
  deleteFunction,
  getDefaultLatencyControl,
  parametersAdapter,
} from '../functions';
import type { GraphChangeType } from './synchronization';
import {
  codeModifier,
  regexParser,
  syncCodeFromGraph,
  syncGraphFromCode,
} from './synchronization';
import { isStepWithConditions } from './types/steps';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Shared logic for removing a step and cleaning up conditions + code references.
 * Used by deleteStep (and deprecated deleteNoCodeStep) reducer handlers.
 */
const removeStepAndCleanupReferences = (flow: Flow, stepId: string) => {
  // Remove step
  flow.steps = flowStepsAdapter.removeOne(flow.steps, stepId);

  // Clean up transition function references
  updateFunctionReferences(flow, stepId, {});

  // Remove conditions in other steps that reference this deleted step
  // Also comment out code blocks in function steps
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
  Object.values(flow.steps.entities).forEach(step => {
    if (!step || step.type === 'advanced_step') {
      return;
    }

    const conditionsToRemove: Condition[] = [];
    const remainingConditions: Condition[] = [];
    step.conditions.forEach((condition: Condition) => {
      if (
        condition.config.$case !== 'exitFlowCondition' &&
        condition.config.value.childStepId === stepId
      ) {
        conditionsToRemove.push(condition);
      } else {
        remainingConditions.push(condition);
      }
    });

    // Only update if conditions were actually removed
    if (conditionsToRemove.length > 0) {
      // Update step conditions using adapter
      flowStepsAdapter.updateOne(flow.steps, {
        id: step.id,
        changes: {
          conditions: remainingConditions,
        },
      });

      // If this is a function step, sync code from graph (comment out removed conditions)
      if (step.type === 'function_step') {
        const functionStep = step as FunctionStep;
        let updatedCode = functionStep.function.code || '';

        // Comment out each removed condition
        for (const condition of conditionsToRemove) {
          const { code } = syncCodeFromGraph(
            flow,
            functionStep,
            updatedCode,
            {
              type:
                condition.config.$case === 'exitFlowCondition'
                  ? 'exit_flow_condition_deleted'
                  : 'condition_deleted',
              conditionId: condition.id,
              condition,
            },
            { source: 'graph' },
          );
          updatedCode = code;
        }

        if (updatedCode !== functionStep.function.code) {
          flowStepsAdapter.updateOne(flow.steps, {
            id: step.id,
            changes: {
              function: {
                ...functionStep.function,
                code: updatedCode,
              },
            },
          });
        }
      }
    }
  });
};

const getDefaultAsrBiasing = (): StepAsrBiasing => ({
  alphanumeric: false,
  nameSpelling: false,
  numeric: false,
  partySize: false,
  preciseDate: false,
  relativeDate: false,
  singleNumber: false,
  time: false,
  yesNo: false,
  address: false,
  customKeywords: [],
  isEnabled: false,
});

const getDefaultDTMFConfig = (): StepDTMFConfig => ({
  isEnabled: false,
  interDigitTimeout: 0,
  maxDigits: 0,
  endKey: '#',
  collectWhileAgentSpeaking: false,
  isPii: false,
});

const updateFunctionReferences = (
  flow: Flow,
  stepId: string,
  transitionFunctionRefs: Record<string, boolean>,
) => {
  Object.values(flow.transitionFunctions.entities).forEach(func => {
    if (!func) {
      return;
    }

    // Step references this function
    if (transitionFunctionRefs[func.id]) {
      func.references =
        func.references ?? transitionFunctionRefAdapter.createInitialState();
      func.references.flowSteps[stepId] = true;
    }
    // Function references this step but should no longer
    else if (func.references?.flowSteps?.[stepId]) {
      delete func.references.flowSteps[stepId];
    }
  });
};

const createAdvancedStep = (
  stepData: CreateFlowStepPayload['step'],
  action: PayloadAction<unknown>,
): AdvancedStep => {
  const step: AdvancedStep = {
    type: 'advanced_step',
    ...stepData,
    asrBiasing: stepData.asrBiasing ?? getDefaultAsrBiasing(),
    dtmfConfig: stepData.dtmfConfig ?? getDefaultDTMFConfig(),
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
  };
  return assignMetadata(action, step);
};

const createDefaultStep = (
  stepData: CreateNoCodeStepPayload,
  action: PayloadAction<unknown>,
): DefaultStep => {
  const step: DefaultStep = {
    type: 'default_step',
    id: stepData.stepId,
    name: stepData.name,
    prompt: stepData.prompt,
    position: stepData.position,
    references:
      stepData.references ?? defaultStepRefAdapter.createInitialState(),
    conditions: [],
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
  };

  return assignMetadata(action, step);
};

type InlineFunctionDataInput = z.infer<
  typeof import('@polyai/stream/commands/handlers/flow/schema').CreateFunctionStepPayloadSchema
>['function'];

const createInlineFunction = (
  funcData: InlineFunctionDataInput,
  action: PayloadAction<unknown>,
): FunctionStep['function'] => {
  const latencyControl = funcData.latencyControl
    ? (() => {
        const delayResponses = (
          funcData.latencyControl?.delayResponses ?? []
        ).map(dr =>
          assignMetadata(action, {
            ...dr,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          }),
        );

        const lc = {
          ...funcData.latencyControl,
          delayResponses: delayResponsesAdapter.getInitialState(
            {},
            delayResponses,
          ),
          createdAt: '',
          createdBy: '',
          updatedAt: '',
          updatedBy: '',
        };
        return assignMetadata(action, lc);
      })()
    : assignMetadata(action, {
        ...getDefaultLatencyControl('', ''),
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      });

  const func = {
    ...funcData,
    latencyControl,
    errors: funcData.errors ?? [],
    archived: funcData.archived ?? false,
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
  };

  return assignMetadata(action, func);
};

const buildLatencyControlForUpdate = (
  updateFunction: Partial<InlineFunctionDataInput> | undefined,
  step: FunctionStep,
) => {
  if (updateFunction?.latencyControl) {
    return updateFunction.latencyControl;
  }

  const existingLatencyControl = step.function.latencyControl;
  if (!existingLatencyControl) {
    return {
      enabled: false,
      delayResponses: [],
      initialDelay: 0,
      interval: 0,
    };
  }

  const delayResponses = delayResponsesAdapter
    .getSelectors()
    .selectAll(existingLatencyControl.delayResponses)
    .map(
      ({
        createdAt: _ca,
        createdBy: _cb,
        updatedAt: _ua,
        updatedBy: _ub,
        ...dr
      }) => ({
        ...dr,
      }),
    );

  return {
    enabled: existingLatencyControl.enabled,
    initialDelay: existingLatencyControl.initialDelay,
    interval: existingLatencyControl.interval,
    delayResponses,
  };
};

/**
 * When a new step is added, other function steps may already contain
 * goto_step("newStepName") calls that couldn't be resolved at creation
 * time (the target didn't exist yet). Re-sync those steps so their
 * conditions are created now that the target is available.
 */
const resolveForwardGotoStepReferences = (
  flow: Flow,
  newStepId: string,
  newStepName: string,
): void => {
  for (const existingStep of Object.values(flow.steps.entities)) {
    if (
      !existingStep ||
      existingStep.id === newStepId ||
      existingStep.type !== 'function_step'
    ) {
      continue;
    }
    const fnStep = existingStep as FunctionStep;
    const existingCode = fnStep.function.code;
    if (
      !existingCode ||
      !regexParser
        .extractConditions(existingCode)
        .some(c => c.stepName === newStepName)
    ) {
      continue;
    }
    const { conditions: resolved } = syncGraphFromCode(
      flow,
      fnStep,
      existingCode,
    );
    if (resolved.length !== fnStep.conditions.length) {
      flowStepsAdapter.updateOne(flow.steps, {
        id: fnStep.id,
        changes: { conditions: resolved },
      });
    }
  }
};

const createFunctionStep = (
  stepData: Omit<
    FunctionStep,
    'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy'
  >,
  action: PayloadAction<unknown>,
): FunctionStep => {
  const step: FunctionStep = {
    type: 'function_step',
    id: stepData.id,
    name: stepData.name,
    position: stepData.position,
    function: stepData.function,
    conditions: [],
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
  };
  return assignMetadata(action, step);
};

const createFlowFunction = (
  funcData:
    | ImportFlowPayload['transitionFunctions'][number]
    | CreateTransitionFunctionPayload['transitionFunction'],
  action: PayloadAction<unknown>,
): FlowFunction => {
  const parameters = (funcData.parameters ?? []).map(p =>
    assignMetadata(action, {
      ...p,
      createdAt: '',
      createdBy: '',
      updatedAt: '',
      updatedBy: '',
    }),
  );

  const latencyControl = funcData.latencyControl
    ? (() => {
        const delayResponses = (
          funcData.latencyControl?.delayResponses ?? []
        ).map(dr =>
          assignMetadata(action, {
            ...dr,
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          }),
        );

        const lc = {
          ...funcData.latencyControl,
          delayResponses: delayResponsesAdapter.getInitialState(
            {},
            delayResponses,
          ),
          createdAt: '',
          createdBy: '',
          updatedAt: '',
          updatedBy: '',
        };
        return assignMetadata(action, lc);
      })()
    : assignMetadata(action, {
        ...getDefaultLatencyControl('', ''),
        createdAt: '',
        createdBy: '',
        updatedAt: '',
        updatedBy: '',
      });

  const func: FlowFunction = {
    ...funcData,
    parameters: parametersAdapter.getInitialState({}, parameters),
    latencyControl,
    errors: funcData.errors ?? [],
    references:
      funcData.references ?? transitionFunctionRefAdapter.createInitialState(),
    archived: funcData.archived ?? false,
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
  };

  return assignMetadata(action, func);
};

const createFlowWithStates = (
  flowData: CreateFlowPayload,
  action: PayloadAction<unknown>,
): Flow => {
  // Create steps - both advanced and default steps go into unified adapter
  const advancedSteps: AdvancedStep[] = flowData.steps.map(s =>
    createAdvancedStep(s, action),
  );
  const defaultSteps: DefaultStep[] = flowData.noCodeSteps.map(s =>
    createDefaultStep(
      {
        flowId: flowData.id,
        stepId: s.stepId,
        name: s.name,
        prompt: s.prompt,
        position: s.position,
        references: s.references,
      },
      action,
    ),
  );
  const allSteps: Step[] = [...advancedSteps, ...defaultSteps];

  // Create functions
  const functions = flowData.transitionFunctions.map(f =>
    createFlowFunction(f, action),
  );

  // Build function-to-step references
  const functionRefsMap: Record<string, Record<string, boolean>> = {};
  advancedSteps.forEach(step => {
    const refs = step.references?.transitionFunctions ?? {};
    Object.keys(refs).forEach(funcId => {
      if (!functionRefsMap[funcId]) {
        functionRefsMap[funcId] = {};
      }
      functionRefsMap[funcId][step.id] = true;
    });
  });

  // Apply function references
  functions.forEach(func => {
    if (functionRefsMap[func.id]) {
      func.references = {
        flowSteps: functionRefsMap[func.id],
        variables: {},
      };
    }
  });

  const flow: Flow = {
    id: flowData.id,
    name: flowData.name,
    description: flowData.description,
    startStepId: flowData.startStepId,
    steps: flowStepsAdapter.getInitialState({}, allSteps),
    transitionFunctions: transitionFunctionsAdapter.getInitialState(
      {},
      functions,
    ),
    createdAt: '',
    createdBy: '',
    updatedAt: '',
    updatedBy: '',
  };

  return assignMetadata(action, flow);
};

const getDefaultStep = (flow: Flow, stepId: string): DefaultStep | null => {
  const step = flow.steps.entities[stepId];
  if (!step || step.type !== 'default_step') {
    return null;
  }
  return step;
};

const getAdvancedStep = (flow: Flow, stepId: string): AdvancedStep | null => {
  const step = flow.steps.entities[stepId];
  if (!step || step.type !== 'advanced_step') {
    return null;
  }
  return step;
};

const getFunctionStep = (flow: Flow, stepId: string): FunctionStep | null => {
  const step = flow.steps.entities[stepId];
  if (!step || step.type !== 'function_step') {
    return null;
  }
  return step;
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const escapeStepName = (code: string, oldName: string, newName: string) => {
  const escapedOld = escapeRegExp(oldName);
  const safeNewForDouble = escapeForDoubleQuotedString(newName);
  const safeNewForSingle = escapeForSingleQuotedString(newName);

  return code
    .replace(new RegExp(`"${escapedOld}"`, 'gu'), `"${safeNewForDouble}"`)
    .replace(new RegExp(`'${escapedOld}'`, 'gu'), `'${safeNewForSingle}'`);
};

// ============================================================================
// Slice
// ============================================================================

const slice = createSlice({
  name: 'flows',
  initialState: {
    flows: flowsAdapter.getInitialState(),
  },
  reducers: {},
  extraReducers: builder => {
    // ========================================================================
    // Flow Management
    // ========================================================================

    builder.addCase(
      createFlow,
      (state, action: PayloadAction<CreateFlowPayload>) => {
        const flow = createFlowWithStates(action.payload, action);
        flowsAdapter.addOne(state.flows, flow);
      },
    );

    builder.addCase(
      updateFlow,
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
      (state, action: PayloadAction<UpdateFlowPayload>) => {
        const { flowId, oldFlowName, ...update } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }
        const previousFlowName = oldFlowName ?? flow.name;
        assignDefinedFields(flow, update);
        assignMetadata(action, flow);

        // Update conv.goto_flow references when the flow name changes
        const newFlowName = update.name;
        if (newFlowName && previousFlowName !== newFlowName) {
          for (const updatedFlow of Object.values(state.flows.entities)) {
            if (!updatedFlow) {
              continue;
            }

            // Update transition function code
            for (const func of Object.values(
              updatedFlow.transitionFunctions.entities,
            )) {
              if (!func?.code) {
                continue;
              }
              const updatedCode = replaceFlowNameInGotoFlow(
                func.code,
                previousFlowName,
                newFlowName,
              );
              if (updatedCode !== func.code) {
                func.code = updatedCode;
              }
            }

            // Update function step code
            for (const step of Object.values(updatedFlow.steps.entities)) {
              if (step?.type !== 'function_step') {
                continue;
              }
              const functionStep = step as FunctionStep;
              if (!functionStep.function?.code) {
                continue;
              }
              const updatedCode = replaceFlowNameInGotoFlow(
                functionStep.function.code,
                previousFlowName,
                newFlowName,
              );
              if (updatedCode !== functionStep.function.code) {
                functionStep.function.code = updatedCode;
              }
            }
          }
        }
      },
    );

    builder.addCase(
      deleteFlow,
      (state, { payload: { flowId } }: PayloadAction<DeleteFlowPayload>) => {
        flowsAdapter.removeOne(state.flows, flowId);
      },
    );

    builder.addCase(
      importFlow,
      (state, action: PayloadAction<ImportFlowPayload>) => {
        const { flowId, steps, transitionFunctions, noCodeSteps } =
          action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        // Import advanced steps
        const advancedSteps: AdvancedStep[] = steps.map(s => {
          const step: AdvancedStep = {
            type: 'advanced_step',
            ...s,
            asrBiasing: s.asrBiasing ?? getDefaultAsrBiasing(),
            dtmfConfig: s.dtmfConfig ?? getDefaultDTMFConfig(),
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };
          return assignMetadata(action, step);
        });

        // Import default steps
        const defaultSteps: DefaultStep[] = noCodeSteps.map(s => {
          const step: DefaultStep = {
            type: 'default_step',
            id: s.id,
            name: s.name,
            prompt: s.prompt,
            position: s.position,
            references:
              s.references ?? defaultStepRefAdapter.createInitialState(),
            conditions: s.conditions ?? [],
            createdAt: '',
            createdBy: '',
            updatedAt: '',
            updatedBy: '',
          };
          return assignMetadata(action, step);
        });

        // Add all steps to unified adapter
        flow.steps = flowStepsAdapter.addMany(flow.steps, [
          ...advancedSteps,
          ...defaultSteps,
        ]);

        // Import functions
        const functions = transitionFunctions.map(f =>
          createFlowFunction(f, action),
        );
        flow.transitionFunctions = transitionFunctionsAdapter.addMany(
          flow.transitionFunctions,
          functions,
        );

        assignMetadata(action, flow);
      },
    );

    // ========================================================================
    // Step Management - handle unified steps
    // ========================================================================

    // ========================================================================
    // Advanced Step Management (formerly FlowStep)
    // ========================================================================

    builder.addCase(
      createFlowStep,
      (state, action: PayloadAction<CreateFlowStepPayload>) => {
        const { flowId, step: stepData } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = createAdvancedStep(stepData, action);
        updateFunctionReferences(
          flow,
          step.id,
          step.references?.transitionFunctions ?? {},
        );
        flow.steps = flowStepsAdapter.addOne(flow.steps, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateFlowStep,
      (state, action: PayloadAction<UpdateFlowStepPayload>) => {
        const {
          flowId,
          step: { id, ...update },
        } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getAdvancedStep(flow, id);
        if (!step) {
          return;
        }

        // Handle step name change - update references in function code
        if (update.name && update.name !== step.name) {
          // Update references in transition functions
          Object.values(flow.transitionFunctions.entities).forEach(func => {
            if (!func || !func.code.includes(step.name)) {
              return;
            }

            if (update.name) {
              func.code = escapeStepName(func.code, step.name, update.name);
            }
          });

          // Update references in function step codes
          Object.values(flow.steps.entities).forEach(otherStep => {
            if (
              otherStep &&
              otherStep.type === 'function_step' &&
              otherStep.id !== id
            ) {
              const otherFunctionStep = otherStep as FunctionStep;
              const otherCode = otherFunctionStep.function.code || '';
              const updatedOtherCode =
                codeModifier.updateStepNameInConditionBlocks(
                  otherCode,
                  step.name,
                  update.name!,
                );

              if (updatedOtherCode !== otherCode) {
                flowStepsAdapter.updateOne(flow.steps, {
                  id: otherStep.id,
                  changes: {
                    function: {
                      ...otherFunctionStep.function,
                      code: updatedOtherCode,
                    },
                  },
                });
              }
            }
          });
        }

        assignDefinedFields(step, update);
        assignMetadata(action, step);

        if (update.references) {
          step.references = update.references;
          updateFunctionReferences(
            flow,
            step.id,
            step.references?.transitionFunctions ?? {},
          );
        }

        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      deleteFlowStep,
      (state, action: PayloadAction<DeleteFlowStepPayload>) => {
        const { flowId, stepId } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        removeStepAndCleanupReferences(flow, stepId);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateStepPosition,
      (state, action: PayloadAction<UpdateFlowStepPositionPayload>) => {
        const {
          flowId,
          positionUpdate: { stepId, newPosition },
        } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getAdvancedStep(flow, stepId);
        if (!step) {
          return;
        }

        step.position = newPosition;
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateStepPositions,
      (state, action: PayloadAction<UpdateFlowStepPositionsPayload>) => {
        const { flowId, newPositions } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        newPositions.forEach(({ stepId, newPosition }) => {
          const step = getAdvancedStep(flow, stepId);
          if (step) {
            step.position = newPosition;
            assignMetadata(action, step);
          }
        });

        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateFlowStepDTMFConfig,
      (state, action: PayloadAction<UpdateFlowStepDTMFConfigPayload>) => {
        const { flowId, stepId, dtmfConfig } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getAdvancedStep(flow, stepId);
        if (!step) {
          return;
        }

        // Merge with existing config to ensure all required fields are present
        step.dtmfConfig = {
          ...step.dtmfConfig,
          ...dtmfConfig,
        };
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateFlowStepASRConfig,
      (state, action: PayloadAction<UpdateFlowStepAsrConfigPayload>) => {
        const {
          flowId,
          stepId,
          asrBiasing: { customKeywords, ...update },
        } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getAdvancedStep(flow, stepId);
        if (!step) {
          return;
        }

        assignDefinedFields(step, {
          asrBiasing: {
            ...update,
            customKeywords: customKeywords?.customKeywords,
          },
        });
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    // ========================================================================
    // Default Step Management (formerly NoCodeStep)
    // ========================================================================

    builder.addCase(
      createNoCodeStep,
      (state, action: PayloadAction<CreateNoCodeStepPayload>) => {
        const { flowId } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = createDefaultStep(action.payload, action);
        flow.steps = flowStepsAdapter.addOne(flow.steps, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateNoCodeStep,
      (state, action: PayloadAction<UpdateNoCodeStepPayload>) => {
        const { flowId, stepId, ...update } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getDefaultStep(flow, stepId);
        if (!step) {
          return;
        }

        // Handle step name change - update references in function code
        if (update.name && update.name !== step.name) {
          // Update references in transition functions
          Object.values(flow.transitionFunctions.entities).forEach(func => {
            if (!func || !func.code.includes(step.name)) {
              return;
            }
            if (update.name) {
              func.code = escapeStepName(func.code, step.name, update.name);
            }
          });

          // Update references in function step codes
          Object.values(flow.steps.entities).forEach(otherStep => {
            if (
              otherStep &&
              otherStep.type === 'function_step' &&
              otherStep.id !== stepId
            ) {
              const otherFunctionStep = otherStep as FunctionStep;
              const otherCode = otherFunctionStep.function.code || '';
              const updatedOtherCode =
                codeModifier.updateStepNameInConditionBlocks(
                  otherCode,
                  step.name,
                  update.name!,
                );

              if (updatedOtherCode !== otherCode) {
                flowStepsAdapter.updateOne(flow.steps, {
                  id: otherStep.id,
                  changes: {
                    function: {
                      ...otherFunctionStep.function,
                      code: updatedOtherCode,
                    },
                  },
                });
              }
            }
          });
        }

        assignDefinedFields(step, update);
        assignMetadata(action, step);

        if (update.references) {
          step.references = update.references;
        }

        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      deleteNoCodeStep,
      (state, action: PayloadAction<DeleteNoCodeStepPayload>) => {
        const { flowId, stepId } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        removeStepAndCleanupReferences(flow, stepId);

        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateNoCodeStepPosition,
      (state, action: PayloadAction<UpdateNoCodeStepPositionPayload>) => {
        const { flowId, stepId, newPosition } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getDefaultStep(flow, stepId);
        if (!step) {
          return;
        }

        step.position = newPosition;
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    // ========================================================================
    // Condition Management
    // ========================================================================

    builder.addCase(
      createNoCodeCondition,
      (state, action: PayloadAction<CreateNoCodeConditionPayload>) => {
        const { flowId, stepId, conditionId, ...conditionData } =
          action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getDefaultStep(flow, stepId);
        if (!step) {
          return;
        }

        const condition: Condition = {
          id: conditionId,
          config: conditionData.config,
        };
        step.conditions.push(condition);
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateNoCodeCondition,
      (state, action: PayloadAction<UpdateNoCodeConditionPayload>) => {
        const { flowId, stepId, conditionId, ...update } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getDefaultStep(flow, stepId);
        if (!step) {
          return;
        }

        const condition = step.conditions.find(c => c.id === conditionId);
        if (!condition) {
          return;
        }

        assignDefinedFields(condition, update);
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      deleteNoCodeCondition,
      (state, action: PayloadAction<DeleteNoCodeConditionPayload>) => {
        const { flowId, stepId, conditionId } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getDefaultStep(flow, stepId);
        if (!step) {
          return;
        }

        const conditionIndex = step.conditions.findIndex(
          c => c.id === conditionId,
        );
        if (conditionIndex === -1) {
          return;
        }

        step.conditions.splice(conditionIndex, 1);
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateNoCodeConditionPosition,
      (state, action: PayloadAction<UpdateNoCodeConditionPositionPayload>) => {
        const { flowId, stepId, conditionId, newPosition } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getDefaultStep(flow, stepId);
        if (!step) {
          return;
        }

        const condition = step.conditions.find(
          (c: Condition) => c.id === conditionId,
        );
        if (!condition) {
          return;
        }

        // Update position immutably through the condition config
        condition.config.value.details.position = newPosition;

        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateNoCodeConditionExitFlowPosition,
      (
        state,
        action: PayloadAction<UpdateNoCodeConditionExitFlowPositionPayload>,
      ) => {
        const { flowId, stepId, conditionId, newPosition } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = getDefaultStep(flow, stepId);
        if (!step) {
          return;
        }

        const condition = step.conditions.find(
          (c: Condition) => c.id === conditionId,
        );
        if (!condition || condition.config.$case !== 'exitFlowCondition') {
          return;
        }

        condition.config.value.exitFlowPosition = newPosition;

        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    // ========================================================================
    // Generic Step Management
    // ========================================================================

    builder.addCase(
      createStep,
      (state, action: PayloadAction<CreateStepPayload>) => {
        const payload = action.payload;
        const { flowId, payload: stepPayload } = payload;

        switch (stepPayload.$case) {
          case 'functionStep': {
            const {
              id: stepId,
              name,
              position,
              function: funcData,
            } = stepPayload.value;
            const flow = state.flows.entities[flowId];
            if (!flow) {
              return;
            }

            const functionWithMeta = createInlineFunction(funcData, action);

            const step = createFunctionStep(
              {
                type: 'function_step',
                id: stepId,
                name,
                position,
                function: functionWithMeta,
                conditions: [],
              },
              action,
            );

            flowStepsAdapter.addOne(flow.steps, step);

            // Sync conditions from code (e.g. goto_step calls) so that edges
            // are drawn immediately when the step is created via ADK push or
            // flow duplication, without requiring a subsequent edit.
            const code = funcData.code;
            if (code && code.trim().length > 0) {
              const { conditions } = syncGraphFromCode(flow, step, code);
              if (conditions.length > 0) {
                flowStepsAdapter.updateOne(flow.steps, {
                  id: stepId,
                  changes: { conditions },
                });
              }
            }

            // Resolve forward references: other function steps in this flow
            // may have goto_step calls pointing to a step that didn't exist
            // when they were created (e.g. ADK batch with arbitrary order).
            // Now that this step exists, re-sync those steps so their
            // conditions are created.
            resolveForwardGotoStepReferences(flow, stepId, name);

            assignMetadata(action, flow);
            break;
          }
          // Future: Add cases for other step types
          // case "default_step": { ... }
          // case "advanced_step": { ... }
        }
      },
    );

    builder.addCase(
      updateStep,
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
      (state, action: PayloadAction<UpdateStepPayload>) => {
        const payload = action.payload;
        const { flowId, stepId, payload: stepPayload } = payload;

        // Discriminate based on step type
        switch (stepPayload.$case) {
          case 'functionStep': {
            const update = stepPayload.value;
            const flow = state.flows.entities[flowId];
            if (!flow) {
              return;
            }

            const step = getFunctionStep(flow, stepId);
            if (!step) {
              return;
            }

            // Handle step name change - update references in function code
            if (update.name && update.name !== step.name) {
              Object.values(flow.transitionFunctions.entities).forEach(func => {
                if (!func || !func.code.includes(step.name)) {
                  return;
                }
                if (update.name) {
                  func.code = escapeStepName(func.code, step.name, update.name);
                }
              });
            }

            // Create updated step with new values
            const updatedStep: FunctionStep = {
              ...step,
              ...(update.name !== undefined && { name: update.name }),
              ...(update.position !== undefined && {
                position: update.position,
              }),
            };

            // Handle function update separately to preserve creation metadata
            if (update.function !== undefined) {
              const functionCode = update.function.code ?? step.function.code;
              const latencyControl = buildLatencyControlForUpdate(
                update.function.latencyControl
                  ? { latencyControl: update.function.latencyControl }
                  : undefined,
                step,
              );
              const errors: FunctionStep['function']['errors'] =
                update.function.errors?.errors ?? step.function.errors;

              const functionPayload: InlineFunctionDataInput = {
                ...update.function,
                id: step.function.id,
                name: updatedStep.name,
                code: functionCode,
                archived: step.function.archived,
                errors,
                latencyControl,
              };

              const functionWithMeta = createInlineFunction(
                functionPayload,
                action,
              );
              // Preserve original creation metadata
              functionWithMeta.createdAt = step.function.createdAt;
              functionWithMeta.createdBy = step.function.createdBy;
              assignMetadata(action, functionWithMeta);
              updatedStep.function = functionWithMeta;

              // Extract conditions from code and sync with step conditions
              // This will also create missing steps referenced in goto_step calls
              // Use the current step from state (not updatedStep) to ensure we see
              // conditions that were just added by createStepCondition commands
              // This prevents duplicate conditions when both createStepCondition and
              // updateStep are dispatched together
              const stepForSync =
                step && isStepWithConditions(step) ? step : updatedStep;

              const { conditions } = syncGraphFromCode(
                flow,
                stepForSync,
                functionCode,
              );

              updatedStep.conditions = conditions;
            }

            assignMetadata(action, updatedStep);
            flowStepsAdapter.updateOne(flow.steps, {
              id: stepId,
              changes: updatedStep,
            });
            assignMetadata(action, flow);
            break;
          }
          // Future: Add cases for other step types
          // case "advanced_step": { break; }
          // case "default_step": { break; }
        }
      },
    );

    builder.addCase(
      deleteStep,
      (state, action: PayloadAction<DeleteGenericStepPayload>) => {
        const { flowId, stepId } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        removeStepAndCleanupReferences(flow, stepId);

        assignMetadata(action, flow);
      },
    );

    // ========================================================================
    // Step Condition Management
    // ========================================================================

    builder.addCase(
      createStepCondition,
      (state, action: PayloadAction<CreateStepConditionPayload>) => {
        const { flowId, stepId, conditionId, ...conditionData } =
          action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = flow.steps.entities[stepId];
        if (!step || !isStepWithConditions(step)) {
          return;
        }

        const condition: Condition = {
          id: conditionId,
          config: conditionData.config,
        };
        step.conditions.push(condition);
        assignMetadata(action, step);
        assignMetadata(action, flow);

        // If step is a function step, sync code from graph
        if (step.type === 'function_step') {
          const functionStep = step as FunctionStep;
          const existingCode = functionStep.function.code || '';
          const { code: updatedCode } = syncCodeFromGraph(
            flow,
            functionStep,
            existingCode,
            {
              type:
                condition.config.$case === 'exitFlowCondition'
                  ? 'exit_flow_condition_added'
                  : 'condition_added',
              condition,
            },
            { source: 'graph' }, // Mark as graph-originated
          );

          if (updatedCode !== existingCode) {
            // Create updated function with new code
            const updatedFunction = {
              ...functionStep.function,
              code: updatedCode,
            };
            assignMetadata(action, updatedFunction);

            flowStepsAdapter.updateOne(flow.steps, {
              id: stepId,
              changes: {
                function: updatedFunction,
              },
            });
            assignMetadata(action, flow);
          }
        }
      },
    );

    builder.addCase(
      updateStepCondition,
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
      (state, action: PayloadAction<UpdateStepConditionPayload>) => {
        const { flowId, stepId, conditionId, ...update } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = flow.steps.entities[stepId];
        if (!step || !isStepWithConditions(step)) {
          return;
        }

        const conditionIndex = step.conditions.findIndex(
          c => c.id === conditionId,
        );
        if (conditionIndex === -1) {
          return;
        }

        if (update.config) {
          const oldCondition = { ...step.conditions[conditionIndex] };
          step.conditions[conditionIndex].config = update.config;
          const newCondition = step.conditions[conditionIndex];

          // If step is a function step, update the code block if condition changed
          if (step.type === 'function_step') {
            const functionStep = step as FunctionStep;
            const existingCode = functionStep.function.code || '';

            // Check if childStepId or label changed.
            // Exit flow conditions has nothing to update.
            const oldChildStepId =
              oldCondition.config.$case !== 'exitFlowCondition'
                ? oldCondition.config.value.childStepId
                : undefined;
            const newChildStepId =
              newCondition.config.$case !== 'exitFlowCondition'
                ? newCondition.config.value.childStepId
                : undefined;

            const oldLabel = oldCondition.config.value.details.label;
            const newLabel = newCondition.config.value.details.label;

            if (oldChildStepId !== newChildStepId || oldLabel !== newLabel) {
              // Condition changed, sync code from graph
              let changeType: GraphChangeType;
              let change:
                | {
                    type: GraphChangeType;
                    condition: Condition;
                    oldLabel?: string;
                    newLabel?: string;
                    oldStepName?: string;
                    newStepName?: string;
                  }
                | undefined;

              if (
                oldChildStepId &&
                newChildStepId &&
                oldChildStepId !== newChildStepId
              ) {
                changeType = 'condition_connection_changed';
                const oldStep = flow.steps.entities[oldChildStepId];
                const newStep = flow.steps.entities[newChildStepId];
                change = {
                  type: changeType,
                  condition: newCondition,
                  oldStepName: oldStep?.name,
                  newStepName: newStep?.name,
                };
              } else if (oldLabel !== newLabel) {
                changeType = 'condition_label_updated';
                change = {
                  type: changeType,
                  condition: newCondition,
                  oldLabel,
                  newLabel,
                };
              }

              if (change) {
                const { code: updatedCode } = syncCodeFromGraph(
                  flow,
                  functionStep,
                  existingCode,
                  change,
                  { source: 'graph' }, // Mark as graph-originated
                );

                if (updatedCode !== existingCode) {
                  flowStepsAdapter.updateOne(flow.steps, {
                    id: stepId,
                    changes: {
                      function: {
                        ...functionStep.function,
                        code: updatedCode,
                      },
                    },
                  });
                }
              }
            }
          }
        }
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      deleteStepCondition,
      (state, action: PayloadAction<DeleteStepConditionPayload>) => {
        const { flowId, stepId, conditionId } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = flow.steps.entities[stepId];
        if (!step || !isStepWithConditions(step)) {
          return;
        }

        const conditionIndex = step.conditions.findIndex(
          c => c.id === conditionId,
        );
        if (conditionIndex === -1) {
          return;
        }

        const deletedCondition = step.conditions[conditionIndex];
        step.conditions.splice(conditionIndex, 1);
        assignMetadata(action, step);

        // If step is a function step, sync code from graph
        if (step.type === 'function_step') {
          const functionStep = step as FunctionStep;
          const existingCode = functionStep.function.code || '';
          const changeType =
            deletedCondition.config.$case === 'exitFlowCondition'
              ? 'exit_flow_condition_deleted'
              : 'condition_deleted';

          const { code: updatedCode } = syncCodeFromGraph(
            flow,
            functionStep,
            existingCode,
            {
              type: changeType,
              conditionId: deletedCondition.id,
              condition: deletedCondition,
            },
            { source: 'graph' }, // Mark as graph-originated
          );

          if (updatedCode !== existingCode) {
            flowStepsAdapter.updateOne(flow.steps, {
              id: stepId,
              changes: {
                function: {
                  ...functionStep.function,
                  code: updatedCode,
                },
              },
            });
          }
        }

        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateStepConditionPosition,
      (state, action: PayloadAction<UpdateStepConditionPositionPayload>) => {
        const { flowId, stepId, conditionId, newPosition } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const step = flow.steps.entities[stepId];
        if (!step || !isStepWithConditions(step)) {
          return;
        }

        const condition = step.conditions.find(
          (c: Condition) => c.id === conditionId,
        );
        if (!condition) {
          return;
        }

        condition.config.value.details.position = newPosition;
        assignMetadata(action, step);
        assignMetadata(action, flow);
      },
    );

    // ========================================================================
    // Function Management
    // ========================================================================

    builder.addCase(
      createFlowTransitionFunction,
      (state, action: PayloadAction<CreateTransitionFunctionPayload>) => {
        const { flowId, transitionFunction: fn } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const func = createFlowFunction(fn, action);
        flow.transitionFunctions = transitionFunctionsAdapter.addOne(
          flow.transitionFunctions,
          func,
        );
        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateFlowTransitionFunction,
      (state, action: PayloadAction<UpdateFlowTransitionFunctionPayload>) => {
        const {
          flowId,
          transitionFunction: { id: functionId, parameters, ...update },
        } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const func = flow.transitionFunctions.entities[functionId];
        if (!func) {
          return;
        }

        assignDefinedFields(func, { ...update, errors: update.errors?.errors });
        assignMetadata(action, func);

        if (parameters) {
          const params = parameters.parameters.map(p =>
            assignMetadata(action, {
              ...p,
              createdAt: '',
              createdBy: '',
              updatedAt: '',
              updatedBy: '',
            }),
          );
          func.parameters = parametersAdapter.getInitialState({}, params);
        }

        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      deleteFlowTransitionFunction,
      (state, action: PayloadAction<DeleteTransitionFunctionPayload>) => {
        const { flowId, functionId } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        flow.transitionFunctions.entities[functionId].archived = true;

        assignMetadata(action, flow);
      },
    );

    builder.addCase(
      updateFlowTransitionFunctionLatencyControl,
      (
        state,
        action: PayloadAction<UpdateFlowTransitionFunctionLatencyControlPayload>,
      ) => {
        const {
          flowId,
          latencyControl: { functionId, delayResponses, ...update },
        } = action.payload;
        const flow = state.flows.entities[flowId];
        if (!flow) {
          return;
        }

        const func = flow.transitionFunctions.entities[functionId];
        if (!func) {
          return;
        }

        assignDefinedFields(func, { latencyControl: update });
        assignMetadata(action, func.latencyControl);

        if (delayResponses) {
          const delays = delayResponses.delayResponses.map(dr =>
            assignMetadata(action, {
              ...dr,
              createdAt: '',
              createdBy: '',
              updatedAt: '',
              updatedBy: '',
            }),
          );
          func.latencyControl.delayResponses =
            delayResponsesAdapter.getInitialState({}, delays);
        }

        assignMetadata(action, func);
        assignMetadata(action, flow);
      },
    );

    // ========================================================================
    // Global Function Cleanup
    // ========================================================================

    builder.addCase(deleteFunction, (state, action) => {
      const { id: functionId } = action.payload;

      Object.values(state.flows.entities).forEach(flow => {
        if (!flow) {
          return;
        }

        Object.values(flow.steps.entities).forEach(step => {
          if (!step || step.type !== 'advanced_step') {
            return;
          }
          const advStep = step as AdvancedStep;

          if (advStep.references?.globalFunctions?.[functionId]) {
            delete advStep.references.globalFunctions[functionId];
          }
        });
      });
    });

    // ========================================================================
    // Move Component(s)
    // ========================================================================

    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
    builder.addCase(moveFlowComponent, (state, action) => {
      const { flowId, positionDetail } = action.payload;
      const flow = state.flows.entities[flowId];
      if (!flow) {
        return;
      }

      const { detail } = positionDetail;

      switch (detail.$case) {
        case 'flowStep': {
          const step = getAdvancedStep(flow, detail.value.stepId);
          if (step) {
            step.position = detail.value.newPosition;
            assignMetadata(action, step);
          }
          break;
        }

        case 'noCodeStep': {
          const step = getDefaultStep(flow, detail.value.stepId);
          if (step) {
            step.position = detail.value.newPosition;
            assignMetadata(action, step);
          }
          break;
        }

        case 'noCodeStepCondition': {
          const step = getDefaultStep(flow, detail.value.stepId);
          const condition = step?.conditions.find(
            (c: Condition) => c.id === detail.value.conditionId,
          );
          if (condition && step) {
            condition.config.value.details.position = detail.value.newPosition;
            assignMetadata(action, step);
          }
          break;
        }

        case 'noCodeStepConditionExitFlow': {
          const step = getDefaultStep(flow, detail.value.stepId);
          const condition = step?.conditions.find(
            (c: Condition) => c.id === detail.value.conditionId,
          );
          if (
            condition &&
            condition.config.$case === 'exitFlowCondition' &&
            step
          ) {
            condition.config.value.exitFlowPosition = detail.value.newPosition;
            assignMetadata(action, step);
          }
          break;
        }

        case 'functionStep': {
          const step = getFunctionStep(flow, detail.value.stepId);
          if (step) {
            step.position = detail.value.newPosition;
            assignMetadata(action, step);
          }
          break;
        }

        case 'functionStepCondition': {
          const step = getFunctionStep(flow, detail.value.stepId);
          const condition = step?.conditions.find(
            (c: Condition) => c.id === detail.value.conditionId,
          );
          if (step && condition) {
            // Only update position for conditions that have a details.position field
            if (
              condition.config.$case === 'functionStepCondition' ||
              condition.config.$case === 'noCodeStepCondition' ||
              condition.config.$case === 'stepCondition' ||
              condition.config.$case === 'exitFlowCondition'
            ) {
              condition.config.value.details.position =
                detail.value.newPosition;
              assignMetadata(action, step);
            }
          }
          break;
        }

        case 'functionStepConditionExitFlow': {
          const step = getFunctionStep(flow, detail.value.stepId);
          const condition = step?.conditions.find(
            (c: Condition) => c.id === detail.value.conditionId,
          );
          if (
            condition &&
            condition.config.$case === 'exitFlowCondition' &&
            step
          ) {
            condition.config.value.exitFlowPosition = detail.value.newPosition;
            assignMetadata(action, step);
          }
          break;
        }
      }

      assignMetadata(action, flow);
    });

    builder.addCase(moveFlowComponents, (state, action) => {
      const { flowId, positionDetails } = action.payload;
      const flow = state.flows.entities[flowId];
      if (!flow) {
        return;
      }

      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
      positionDetails.forEach(positionDetail => {
        const { detail } = positionDetail;

        switch (detail.$case) {
          case 'flowStep': {
            const step = getAdvancedStep(flow, detail.value.stepId);
            if (step) {
              step.position = detail.value.newPosition;
              assignMetadata(action, step);
            }
            break;
          }

          case 'noCodeStep': {
            const step = getDefaultStep(flow, detail.value.stepId);
            if (step) {
              step.position = detail.value.newPosition;
              assignMetadata(action, step);
            }
            break;
          }

          case 'noCodeStepCondition': {
            const step = getDefaultStep(flow, detail.value.stepId);
            const condition = step?.conditions.find(
              (c: Condition) => c.id === detail.value.conditionId,
            );
            if (condition && step) {
              condition.config.value.details.position =
                detail.value.newPosition;
              assignMetadata(action, step);
            }
            break;
          }

          case 'noCodeStepConditionExitFlow': {
            const step = getDefaultStep(flow, detail.value.stepId);
            const condition = step?.conditions.find(
              (c: Condition) => c.id === detail.value.conditionId,
            );
            if (
              condition &&
              condition.config.$case === 'exitFlowCondition' &&
              step
            ) {
              condition.config.value.exitFlowPosition =
                detail.value.newPosition;
              assignMetadata(action, step);
            }
            break;
          }

          case 'functionStep': {
            const step = getFunctionStep(flow, detail.value.stepId);
            if (step) {
              step.position = detail.value.newPosition;
              assignMetadata(action, step);
            }
            break;
          }

          case 'functionStepCondition': {
            const step = getFunctionStep(flow, detail.value.stepId);
            const condition = step?.conditions.find(
              (c: Condition) => c.id === detail.value.conditionId,
            );
            if (condition && step) {
              condition.config.value.details.position =
                detail.value.newPosition;
              assignMetadata(action, step);
            }
            break;
          }

          case 'functionStepConditionExitFlow': {
            // For function step exit flow conditions, we update the exit flow position.
            // Note: The condition node itself is not rendered in the UI (we connect
            // directly from function step to exit flow node), but the condition still
            // exists in the data model for code generation purposes.
            const step = getFunctionStep(flow, detail.value.stepId);
            if (!step) {
              break;
            }
            const condition = step.conditions.find(
              (c: Condition) => c.id === detail.value.conditionId,
            );
            if (condition && condition.config.$case === 'exitFlowCondition') {
              condition.config.value.exitFlowPosition =
                detail.value.newPosition;
              assignMetadata(action, step);
            }
            break;
          }
        }
      });

      assignMetadata(action, flow);
    });

    // ========================================================================
    // Entity Reference Management
    // ========================================================================

    builder.addCase(deleteEntity, (state, action) => {
      const deletedEntityId = action.payload.id;

      Object.values(state.flows.entities).forEach(flow => {
        if (!flow) {
          return;
        }

        // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: existing
        Object.values(flow.steps.entities).forEach(step => {
          if (!step) {
            return;
          }

          if (step.type === 'default_step') {
            const defaultStep = step as DefaultStep;

            // Remove entity from required entities in conditions
            defaultStep.conditions.forEach((condition: Condition) => {
              const requiredEntities =
                condition.config.value.details.requiredEntities;
              const index = requiredEntities.indexOf(deletedEntityId);
              if (index > -1) {
                requiredEntities.splice(index, 1);
              }
            });

            // Remove entity reference
            if (defaultStep.references?.entities?.[deletedEntityId]) {
              delete defaultStep.references.entities[deletedEntityId];
            }
          } else if (step.type === 'advanced_step') {
            const advancedStep = step as AdvancedStep;

            // Remove entity reference
            if (advancedStep.references?.entities?.[deletedEntityId]) {
              delete advancedStep.references.entities[deletedEntityId];
            }
          }
        });
      });
    });

    builder.addCase(createEntity, (state, action) => {
      const createdEntityId = action.payload.id;
      const { flowSteps, noCodeSteps } = action.payload.references ?? {};

      // Add references to advanced steps (flowSteps)
      if (flowSteps) {
        Object.keys(flowSteps).forEach(reference => {
          const [flowId, stepId] = reference.split('/');
          const flow = state.flows.entities[flowId];
          const step = flow?.steps.entities[stepId];

          if (step && step.type === 'advanced_step') {
            const advStep = step as AdvancedStep;
            if (!advStep.references) {
              advStep.references = advancedStepRefAdapter.createInitialState();
            }
            advStep.references.entities[createdEntityId] = true;
          }
        });
      }

      // Add references to default steps (noCodeSteps)
      if (noCodeSteps) {
        Object.keys(noCodeSteps).forEach(reference => {
          const [flowId, stepId] = reference.split('/');
          const flow = state.flows.entities[flowId];
          const step = flow?.steps.entities[stepId];

          if (step && step.type === 'default_step') {
            const defStep = step as DefaultStep;
            if (!defStep.references) {
              defStep.references = defaultStepRefAdapter.createInitialState();
            }
            defStep.references.entities[createdEntityId] = true;
          }
        });
      }
    });

    builder.addCase(updateEntity, (state, action) => {
      const updatedEntityId = action.payload.id;
      const { flowSteps, noCodeSteps } = action.payload.references ?? {};

      // Add references to advanced steps (flowSteps)
      if (flowSteps) {
        Object.keys(flowSteps).forEach(reference => {
          const [flowId, stepId] = reference.split('/');
          const flow = state.flows.entities[flowId];
          const step = flow?.steps.entities[stepId];

          if (step && step.type === 'advanced_step') {
            const advStep = step as AdvancedStep;
            if (!advStep.references) {
              advStep.references = advancedStepRefAdapter.createInitialState();
            }
            advStep.references.entities[updatedEntityId] = true;
          }
        });
      }

      // Add references to default steps (noCodeSteps)
      if (noCodeSteps) {
        Object.keys(noCodeSteps).forEach(reference => {
          const [flowId, stepId] = reference.split('/');
          const flow = state.flows.entities[flowId];
          const step = flow?.steps.entities[stepId];

          if (step && step.type === 'default_step') {
            const defStep = step as DefaultStep;
            if (!defStep.references) {
              defStep.references = defaultStepRefAdapter.createInitialState();
            }
            defStep.references.entities[updatedEntityId] = true;
          }
        });
      }
    });
  },
});

// ============================================================================
// Exports
// ============================================================================

// Adapters
export {
  delayResponsesAdapter,
  flowsAdapter,
  flowStepsAdapter,
  parametersAdapter,
  transitionFunctionRefAdapter,
  transitionFunctionsAdapter,
};

// Types
export type {
  AdvancedStep,
  DefaultStep,
  FunctionStep,
  Flow,
  FlowFunction,
  Step,
};

// Selectors
export const flowsSelectors = flowsAdapter.getSelectors();
export const flowStepsSelectors = flowStepsAdapter.getSelectors();
export const transitionFunctionsSelectors =
  transitionFunctionsAdapter.getSelectors();

// Actions
export {
  createFlow,
  createFlowStep,
  createFlowTransitionFunction,
  createNoCodeCondition,
  createNoCodeStep,
  createStep,
  createStepCondition,
  deleteFlow,
  deleteFlowStep,
  deleteFlowTransitionFunction,
  deleteNoCodeStep,
  deleteStep,
  updateFlowStepASRConfig as editFlowStepASRConfig,
  updateFlowStepDTMFConfig as editFlowStepDTMFConfig,
  importFlow,
  moveFlowComponent,
  moveFlowComponents,
  updateFlow,
  updateFlowStep,
  updateFlowTransitionFunction,
  updateFlowTransitionFunctionLatencyControl,
  updateNoCodeCondition,
  updateNoCodeConditionExitFlowPosition,
  updateNoCodeConditionPosition,
  updateNoCodeStep,
  updateNoCodeStepPosition,
  updateStep,
  updateStepCondition,
  updateStepConditionPosition,
  updateStepPosition,
  updateStepPositions,
};

// Legacy exports for backwards compatibility
export { flowStepsSelectors as stepsSelectors };
export { transitionFunctionsSelectors as flowFunctionsSelectors };

// New typed selectors
export {
  selectAdvancedStepById,
  selectAdvancedSteps,
  selectDefaultStepById,
  selectDefaultSteps,
  selectFunctionSteps,
  selectSteps,
} from './selectors';
// Utils
// Exports moved to synchronization module
export { codeInserter } from './synchronization';
// Enums
export { ConditionType, IngressPosition } from './types';
export default slice.reducer;
